/**
 * Day-map geometry for the hero triple-comparison (#545 landing redesign).
 *
 * WHAT THE BARS CLAIM. Under each cell of the homepage hero sits a slim bar
 * whose x-axis is the source day, left to right, showing what that mode did to
 * the day's minutes:
 *
 *   constant  — uniform gray + faint tick marks, every minute the same speed
 *   trim      — teal kept-segments / dark cut-gaps, MotionTrim
 *   pace      — brightness-gradient teal (bright = busy/slow, dim = quiet/fast)
 *
 * PROVENANCE: MEASURED FROM THE THREE SHIPPED CLIPS by
 * `scripts/derive_source_treatment_maps.py`. The constant clip plays every
 * source frame once in order, so its frame i IS source frame i; the other two
 * are built from that same set of frames, so each of their frames matches back
 * to it by nearest-neighbour on a downscaled grayscale thumbnail. The trim bar
 * is the set of source frames that survived; the pace bar is how many source
 * frames each output frame advanced. Nothing below is hand-tuned — the trim
 * bar's kept fraction falls out of the match, not out of a threshold picked
 * until it looked right.
 *
 * RE-RUN THE SCRIPT WHENEVER ANY OF THE THREE CLIPS IS RE-RENDERED. The
 * measured block is machine-written; do not hand-edit it.
 *
 * ONE EXCEPTION, AND IT IS DELIBERATE: `PACE_SPEED` below is the measurement
 * taken from the pace clip as it was BEFORE the burned-in speed dial was
 * switched on, and it is intentionally NOT what a fresh run of the script
 * produces today. The dial is drawn into the pace clip but not into the
 * constant clip the matcher compares against, so it biases every distance in
 * that comparison: re-running against the dial-on clip moves 6 of 379 output
 * frames onto a neighbouring source frame (drift of exactly 1) and shifts 7 of
 * the 72 buckets by up to 0.33x. That is measurement error, not a real change —
 * the dial-on render walks the identical source frames (verified two ways: the
 * speed-curve JSON is byte-identical, and every frame of the new clip aligns to
 * the same-indexed frame of the old one, 377/377, rather than to a neighbour).
 * Since the truth did not move, the pre-dial numbers are the accurate ones.
 *
 * SO: if you re-run the script and `PACE_SPEED` changes while only the pace
 * clip was re-rendered, check whether the overlay explains it before committing
 * the new numbers. `SOURCE_FRAMES`, `TRIM_KEPT` and `TRIM_KEPT_FRACTION` are
 * unaffected — they are measured from the trim clip, which carries no overlay.
 *
 * WHY BAKED: the measurement needs ffmpeg and a full decode of all three clips.
 * That is a build-time job, not a render-time one, and its output is ~2 KB of
 * numbers that turn into CSS gradient strings once at module init.
 */

/* MEASURED-START */
// Generated by scripts/derive_source_treatment_maps.py — do not hand-edit.
const SOURCE_FRAMES = 873;
const TRIM_KEPT_FRACTION = 0.4307;
const TRIM_KEPT = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0, 0, 0, 0, 0, 0, 1, 1, 1, 1, 0, 0, 0, 0, 0, 0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
const PACE_SPEED = [5.417, 6.25, 6.0, 6.5, 5.75, 5.25, 4.833, 3.462, 3.917, 2.583, 2.833, 3.25, 3.083, 1.833, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.5, 2.833, 2.833, 3.846, 4.583, 6.5, 5.833, 5.5, 4.0, 4.75, 2.25, 2.231, 4.25, 3.833, 3.083, 4.0, 3.917, 3.917, 4.0, 3.308, 2.333, 1.833, 1.833, 2.333, 2.833, 2.5, 2.5, 1.923, 1.917, 1.417, 1.5, 1.333, 1.833, 1.917, 2.167, 1.615, 2.25, 3.25, 3.583, 3.5, 3.833, 4.333, 5.0, 5.846, 6.0, 6.417, 5.833, 6.5, 6.667, 6.0, 6.0, 6.0];
/* MEASURED-END */

/* The measured block is machine output. If the script is ever run against a
 * clip it cannot decode it writes empty series, and every export below is built
 * at MODULE SCOPE — so a throw here happens while the landing page is being
 * imported, before any ErrorBoundary exists, and white-screens the whole page
 * rather than degrading one 10px bar. The builders therefore bail to a flat bar
 * and say so for whoever regenerated the data. */
const FLAT_MAP_CSS = 'linear-gradient(90deg, #6a7381, #6a7381)';

if (!TRIM_KEPT.length || !PACE_SPEED.length) {
  // eslint-disable-next-line no-console
  console.warn(
    'sourceTreatmentMapData: the measured block is empty. The hero day-map bars ' +
      'fall back to a flat fill — re-run scripts/derive_source_treatment_maps.py.'
  );
}

// ---- day-map 1: constant — uniform gray, repeating faint ticks --------------
// CSS only: every minute got the same treatment, so there is nothing to measure.
export const CONSTANT_MAP_CSS =
  'repeating-linear-gradient(90deg, rgba(255,255,255,.16) 0 1px, transparent 1px 5.5%), ' +
  'linear-gradient(90deg, #6a7381, #6a7381)';

// ---- day-map 2: MotionTrim — teal keeps, dark gaps -------------------------
// One hard-edged stop pair per measured bucket: teal where that slice of the
// day survived the trim, near-transparent where it was cut.
function buildTrimMap() {
  if (!TRIM_KEPT.length) return FLAT_MAP_CSS;
  const n = TRIM_KEPT.length;
  const stops = TRIM_KEPT.map((kept, i) => {
    const a = ((i / n) * 100).toFixed(3);
    const b = (((i + 1) / n) * 100).toFixed(3);
    return `${kept ? '#00b3a6' : 'rgba(255,255,255,.05)'} ${a}% ${b}%`;
  });
  return `linear-gradient(90deg, ${stops.join(', ')})`;
}

export const TRIM_MAP_CSS = buildTrimMap();

/** Share of the source day MotionTrim kept, for copy that wants to cite it. */
export const TRIM_KEPT_SHARE = TRIM_KEPT_FRACTION;

/** Frames in the source day the bars are indexed by. */
export const SOURCE_FRAME_COUNT = SOURCE_FRAMES;

// ---- day-map 3: MotionPace — brightness-gradient teal -----------------------
// PACE_SPEED[i] is how many source frames the render advanced per output frame
// over that slice of the day — i.e. local playback speed. Bright teal is SLOW
// (the busy minutes MotionPace lingers on), dim slate is FAST (the quiet ones
// it sprints past), which is the inverse of speed.
function buildPaceMap() {
  if (!PACE_SPEED.length) return FLAT_MAP_CSS;
  const n = PACE_SPEED.length;

  // Percentile-anchored rather than min/max: one all-out sprint through a long
  // still stretch would otherwise park the whole rest of the day at the bright
  // end of the ramp.
  const sorted = [...PACE_SPEED].sort((a, b) => a - b);
  const lo = sorted[Math.floor(sorted.length * 0.06)];
  const hi = sorted[Math.floor(sorted.length * 0.9)];

  const stops = PACE_SPEED.map((speed, i) => {
    let t = (speed - lo) / (hi - lo || 1);
    t = 1 - Math.min(1, Math.max(0, t)); // fast -> dim, slow -> bright
    t **= 1.25; // pull the mids down so the real-time stretches pop
    const g = Math.round(36 + (222 - 36) * t);
    const b = Math.round(40 + (206 - 40) * t);
    const a = (((i + 0.5) / n) * 100).toFixed(3);
    return `rgb(8,${g},${b}) ${a}%`;
  });
  return `linear-gradient(90deg, ${stops.join(', ')})`;
}

export const PACE_MAP_CSS = buildPaceMap();
